import { create } from 'zustand'
import type { ThemePreference } from '../api/me'

const NAV_SECTIONS_STORAGE_KEY = 'rpa_nav_sections_v1'
const MARK_ON_OPEN_STORAGE_KEY = 'rpa_mark_on_open'

type NavSectionsState = Record<string, boolean>

const getInitialNavSections = (): NavSectionsState => {
  if (typeof window === 'undefined') {
    return { inbox: true }
  }

  try {
    const raw = window.localStorage.getItem(NAV_SECTIONS_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as unknown
      if (parsed && typeof parsed === 'object') {
        return { inbox: true, ...(parsed as NavSectionsState) }
      }
    }
  } catch (error) {
    console.warn('Unable to load navigation sections state', error)
  }

  return { inbox: true }
}

const persistNavSections = (state: NavSectionsState) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(NAV_SECTIONS_STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.warn('Unable to persist navigation sections state', error)
  }
}

interface UIState {
  themePreference: ThemePreference
  setThemePreference: (preference: ThemePreference) => void
  navSections: NavSectionsState
  setNavSection: (section: string, expanded: boolean) => void
  toggleNavSection: (section: string) => void
  markOnOpen: boolean
  setMarkOnOpen: (value: boolean) => void
}

const getInitialMarkOnOpen = (): boolean => {
  if (typeof window === 'undefined') {
    return true
  }

  try {
    const stored = window.localStorage.getItem(MARK_ON_OPEN_STORAGE_KEY)
    if (stored === null) {
      return true
    }
    return stored === '1'
  } catch (error) {
    console.warn('Unable to load inbox preference state', error)
    return true
  }
}

const persistMarkOnOpen = (value: boolean) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(MARK_ON_OPEN_STORAGE_KEY, value ? '1' : '0')
  } catch (error) {
    console.warn('Unable to persist inbox preference state', error)
  }
}

const useUIStore = create<UIState>((set) => ({
  themePreference: 'system',
  setThemePreference: (themePreference) => set({ themePreference }),
  navSections: getInitialNavSections(),
  setNavSection: (section, expanded) =>
    set((state) => {
      const next = { ...state.navSections, [section]: expanded }
      persistNavSections(next)
      return { navSections: next }
    }),
  toggleNavSection: (section) =>
    set((state) => {
      const next = { ...state.navSections, [section]: !state.navSections[section] }
      persistNavSections(next)
      return { navSections: next }
    }),
  markOnOpen: getInitialMarkOnOpen(),
  setMarkOnOpen: (value) =>
    set(() => {
      persistMarkOnOpen(value)
      return { markOnOpen: value }
    }),
}))

export default useUIStore
